import { supabase } from "@/integrations/supabase/client";

/**
 * Extracts the session_id from the current JWT token's claims.
 * Returns null if no session is active or parsing fails.
 */
export function getSessionIdFromJwt(jwt: string | undefined | null): string | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]));
    return payload.session_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns the current access token and session ID.
 * Useful for building headers for edge function calls.
 */
export async function getSessionContext() {
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token || supabaseKey;
  const sessionId = getSessionIdFromJwt(data.session?.access_token);
  return { accessToken, sessionId, supabaseKey };
}

/**
 * Builds standard headers for edge function fetch calls,
 * always including x-session-id when available.
 */
export async function buildEdgeFunctionHeaders(
  options: { contentType?: string | null } = {}
): Promise<Record<string, string>> {
  const { accessToken, sessionId, supabaseKey } = await getSessionContext();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    apikey: supabaseKey,
  };
  if (options.contentType !== null) {
    headers["Content-Type"] = options.contentType || "application/json";
  }
  if (sessionId) {
    headers["x-session-id"] = sessionId;
  }
  return headers;
}

/**
 * Returns just the x-session-id header object for use with supabase.functions.invoke().
 * Usage: supabase.functions.invoke("fn", { headers: await getSessionHeaders(), body: ... })
 */
export async function getSessionHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const sessionId = getSessionIdFromJwt(data.session?.access_token);
  if (sessionId) return { "x-session-id": sessionId };
  return {};
}
